import { isValidRwandanPhone } from "./paymentHelpers";

export const isUserVerified = (user) => {
  if (!user) return false;
  if (user.phoneNumber && !user.email) return true;
  const value = user.isEmailVerified;
  return value === true || value === 1 || value === "true";
};

export const formatPhoneDisplay = (phone) => {
  if (!phone) return "";
  const raw = String(phone).replace(/\s/g, "");
  if (raw.startsWith("+")) return raw;
  const digits = raw.replace(/\D/g, "");
  if (digits.startsWith("250")) return `+${digits}`;
  if (digits.startsWith("0")) return `+250${digits.slice(1)}`;
  if (digits.length === 9) return `+250${digits}`;
  return raw;
};

export const normalizePhoneForApi = (phone) => {
  const raw = String(phone || "")
    .replace(/\s/g, "")
    .replace(/[^\d+]/g, "");
  let normalized = raw.startsWith("+") ? raw.slice(1) : raw;
  if (normalized.startsWith("0")) normalized = `250${normalized.slice(1)}`;
  else if (/^\d{9}$/.test(normalized)) normalized = `250${normalized}`;
  return normalized;
};

export const isValidEmail = (email) => /\S+@\S+\.\S+/.test(String(email || "").trim());

export const validateProfileContact = (field, value) => {
  const trimmed = String(value || "").trim();
  if (field === "email" && !isValidEmail(trimmed)) {
    return "Enter a valid email address.";
  }
  if (field === "phoneNumber" && !isValidRwandanPhone(trimmed)) {
    return "Enter a valid Rwandan phone number (e.g. 0781234567).";
  }
  if (field === "name" && !trimmed) {
    return "Name cannot be empty.";
  }
  return null;
};

export const USER_LEVEL_LABELS = {
  BEGINNER: "Beginner",
  AMATEUR: "Amateur",
  SEMI_PRO: "Semi-Pro",
  PRO: "Pro",
  LEGEND: "Legend",
  ELITE: "Elite",
};

export const formatUserLevel = (level) =>
  USER_LEVEL_LABELS[String(level || "").toUpperCase()] || "Member";

export const formatSocialCount = (value) => {
  const count = Array.isArray(value) ? value.length : Number(value) || 0;
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
  if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`;
  return String(count);
};

export const PROFILE_CONNECTION_TYPES = {
  followers: {
    id: "followers",
    title: "Followers",
    emptyTitle: "No followers yet",
    emptyText: "When someone follows you, they will show up here.",
  },
  following: {
    id: "following",
    title: "Following",
    emptyTitle: "Not following anyone yet",
    emptyText: "Follow predictors and top players to build your circle.",
  },
};

export const normalizeConnectionListType = (value) =>
  String(value || "").toLowerCase() === "following" ? "following" : "followers";

export const userIsFollowedBy = (followingIds, targetId) =>
  Array.isArray(followingIds) &&
  followingIds.some((id) => String(id) === String(targetId));

export const getConnectionActionMeta = ({ isFollowing, listType }) => {
  if (isFollowing) {
    if (listType === "following") {
      return { label: "Unfollow", variant: "danger" };
    }
    return { label: "Following", variant: "ghost" };
  }
  if (listType === "followers") {
    return { label: "Follow back", variant: "primary" };
  }
  return { label: "Follow", variant: "primary" };
};

export const getConnectionFavoriteChips = (user) =>
  [
    user?.favoriteTeam?.name && { label: user.favoriteTeam.name },
    user?.favoritePlayer?.name && { label: user.favoritePlayer.name },
    user?.favoriteLeague?.name && { label: user.favoriteLeague.name },
  ].filter(Boolean);

export const truncateNote = (note, max = 120) => {
  const text = String(note || "").trim();
  if (!text) return "";
  if (text.length <= max) return text;
  return `${text.slice(0, max).trim()}…`;
};

export const PROFILE_NOTE_MAX_CHARS = 200;

export const maskInviteCode = (code) => (code ? "•••• •••• ••" : "No code yet");

export const PROFILE_SECTIONS = [
  { id: "account", label: "Personal Information", eyebrow: "Account" },
  { id: "favorites", label: "Favorites", eyebrow: "Personalise" },
  { id: "referral", label: "Invitation code", eyebrow: "Refer & earn" },
  { id: "settings", label: "Settings", eyebrow: "Account" },
];

export const PROFILE_SETTINGS_LINK_ROWS = [
  {
    id: "privacy",
    label: "Privacy policy",
    description: "View our privacy policy",
    path: "/app/privacy",
  },
  {
    id: "terms",
    label: "Terms of service",
    description: "Read our terms of service",
    path: "/app/terms",
  },
  {
    id: "community",
    label: "Community guidelines",
    description: "Learn the community rules",
    path: "/app/community",
  },
  {
    id: "password",
    label: "Change password",
    description: "Update your account password",
  },
];

export const PROFILE_FAVORITE_FIELDS = [
  { key: "favoritePlayer", label: "Favorite player", icon: "player" },
  { key: "favoriteTeam", label: "Favorite team", icon: "team" },
  { key: "favoriteLeague", label: "Favorite league", icon: "league" },
];

export const FAVORITE_CATALOG_TYPES = {
  favoritePlayer: "players",
  favoriteTeam: "teams",
  favoriteLeague: "leagues",
};

export const favoriteToCatalogItem = (favorite) => {
  if (!favorite?.name) return null;
  return {
    entityId: favorite.entityId ? String(favorite.entityId) : null,
    apiId: favorite.apiId ?? null,
    name: favorite.name,
    logo: favorite.logo || null,
    meta: null,
    type: favorite.type || null,
    status: favorite.status || null,
  };
};

export const favoriteFieldMeta = (favorite) => {
  if (!favorite?.name) return "Not set yet";
  if (favorite.status === "verified") return "From catalog";
  const year = favorite.updatedAt
    ? new Date(favorite.updatedAt).getFullYear()
    : null;
  return year ? `Updated ${year}` : "Updated recently";
};

export const favoriteDraftsFromUser = (user) => ({
  favoritePlayer: favoriteToCatalogItem(user?.favoritePlayer),
  favoriteTeam: favoriteToCatalogItem(user?.favoriteTeam),
  favoriteLeague: favoriteToCatalogItem(user?.favoriteLeague),
});

export const hasFavoritePayload = (payload) =>
  Object.values(payload).some((value) => {
    if (!value) return false;
    if (typeof value === "object") {
      return Boolean(value.entityId || String(value.name || "").trim());
    }
    return String(value || "").trim().length > 0;
  });

export const favoritePayloadFromDraft = (draft) => {
  if (!draft) return null;
  if (draft.entityId) return { entityId: draft.entityId };
  if (draft.name) return draft.name;
  return null;
};
